import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageShell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PRO_TYPES = ["medico", "infermiere", "oss", "fisioterapista", "assistente_infermieristico", "trasporto_infermi", "badante", "altro_professionista", "struttura_sanitaria", "operatore_amministrativo"];
const USAGE = {
  paziente: "Paziente", medico: "Medico", infermiere: "Infermiere", oss: "OSS", fisioterapista: "Fisioterapista",
  assistente_infermieristico: "Assistente infermieristico", trasporto_infermi: "Ambulanza / Trasporto infermi", badante: "Badante",
  altro_professionista: "Altro professionista sanitario", struttura_sanitaria: "Struttura sanitaria", operatore_amministrativo: "Operatore amministrativo",
};

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  const done = useRef(false);
  const { reloadAccess, refresh } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post("/auth/verify-email", { code: params.get("token") })
      .then(() => { setState("ok"); refresh(); reloadAccess(); })
      .catch(() => setState("error"));
  }, [params, refresh, reloadAccess]);
  return (
    <PageShell title="Verifica email" testid="verify-email-page">
      <div className="glass max-w-lg rounded-3xl p-6" data-testid={`verify-email-${state}`}>
        {state === "loading" && <p>Verifica in corso…</p>}
        {state === "ok" && <p className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Email verificata. Il tuo account è ora di livello L1.</p>}
        {state === "error" && <p className="flex items-center gap-2 text-rose-700"><AlertTriangle className="h-5 w-5" /> Link non valido o scaduto. Richiedine uno nuovo dal profilo.</p>}
      </div>
    </PageShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [f, setF] = useState({ password: "", password_confirm: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), ...f });
      toast.success("Password aggiornata. Tutte le sessioni sono state chiuse.");
      navigate("/accedi");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <PageShell title="Reimposta password" testid="reset-password-page">
      <form onSubmit={submit} className="glass max-w-md space-y-3 rounded-3xl p-6">
        <Label>Nuova password</Label><Input data-testid="reset-password-input" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <Label>Conferma password</Label><Input data-testid="reset-password-confirm-input" type="password" value={f.password_confirm} onChange={(e) => setF({ ...f, password_confirm: e.target.value })} />
        <Button data-testid="reset-password-submit" className="w-full rounded-full bg-sky-600 hover:bg-sky-700">Salva</Button>
      </form>
    </PageShell>
  );
}

export function SandboxIdPPage() {
  const { provider } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, user } = useAuth();
  const [f, setF] = useState(() => ({
    fiscal_code: `TSTSBX${String(Math.floor(Math.random() * 1e8)).padStart(8, "0")}Z${Math.floor(Math.random() * 10)}`.slice(0, 16),
    first_name: "Mario", last_name: "Rossi", email: user?.email || "",
  }));
  useEffect(() => {
    if (user?.email) setF((v) => ({ ...v, email: v.email || user.email }));
  }, [user]);
  const P = provider?.toUpperCase();
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/identity/${provider}/sandbox-callback`, { state: params.get("state"), ...f });
      setUser(data);
      toast.success(`Identità verificata con ${P} (SANDBOX)`);
      const { data: prof } = await api.get("/profile");
      navigate(prof.onboarding_done ? "/profilo?tab=identita" : "/onboarding");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="min-h-screen bg-slate-100 p-4 sm:p-10" data-testid="sandbox-idp-page">
      <div className="mx-auto max-w-lg">
        <div className="mb-4 rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50 p-4 text-sm text-amber-900" data-testid="sandbox-warning">
          <b>AMBIENTE SANDBOX {P} – NON È UNA VERA AUTENTICAZIONE.</b> Questo è un Identity Provider di test simulato. SPID e CIE non sono operativi finché non viene attivata la convenzione con un provider autorizzato.
        </div>
        <form onSubmit={submit} className="space-y-3 rounded-3xl bg-white p-6 shadow-xl">
          <p className="font-display text-xl font-extrabold text-[#0066CC]">{P} · IdP di test</p>
          <p className="text-xs text-slate-500">Attributi restituiti dall'IdP simulato (dati fittizi):</p>
          {[["fiscal_code", "Codice fiscale"], ["first_name", "Nome"], ["last_name", "Cognome"], ["email", "Email"]].map(([k, l]) => (
            <div key={k} className="space-y-1"><Label>{l}</Label><Input data-testid={`sandbox-${k}-input`} required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>
          ))}
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => navigate("/accedi")} className="flex-1 rounded-full">Annulla</Button>
            <Button data-testid="sandbox-authorize-btn" className="flex-1 rounded-full bg-[#0066CC] hover:bg-[#004c99]">Autorizza (sandbox)</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { reloadAccess, refresh } = useAuth();
  const [f, setF] = useState({ first_name: "", last_name: "", usage_type: "" });
  const [done, setDone] = useState(false);
  useEffect(() => {
    api.get("/profile").then(({ data }) => setF((v) => ({ ...v, first_name: data.first_name || "", last_name: data.last_name || "" })));
  }, []);
  const save = async () => {
    try {
      await api.put("/profile", { ...f, onboarding_done: true });
      await refresh();
      reloadAccess();
      if (PRO_TYPES.includes(f.usage_type)) setDone(true);
      else navigate("/");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <PageShell title="Configura il tuo profilo" subtitle="Indica come utilizzerai la clinica virtuale." testid="onboarding-page">
      {done ? (
        <div className="glass max-w-xl space-y-3 rounded-3xl p-6" data-testid="onboarding-professional-notice">
          <p className="text-sm text-slate-700">La scelta di una categoria professionale <b>non attribuisce alcuna autorizzazione</b>. Per offrire prestazioni devi verificare l'identità (SPID/CIE) e completare la certificazione professionale, che sarà revisionata da un amministratore.</p>
          <div className="flex gap-2">
            <Button data-testid="onboarding-go-certification" onClick={() => navigate("/profilo?tab=certificazione")} className="rounded-full bg-sky-600 hover:bg-sky-700">COMPLETA CERTIFICAZIONE</Button>
            <Button variant="outline" onClick={() => navigate("/")} className="rounded-full">Più tardi</Button>
          </div>
        </div>
      ) : (
        <div className="glass max-w-2xl space-y-5 rounded-3xl p-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Nome</Label><Input data-testid="onboarding-firstname" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} /></div>
            <div className="space-y-1"><Label>Cognome</Label><Input data-testid="onboarding-lastname" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {Object.entries(USAGE).map(([k, l]) => (
              <button key={k} data-testid={`onboarding-usage-${k}`} onClick={() => setF({ ...f, usage_type: k })} className={`rounded-xl border px-3 py-3 text-left text-xs font-semibold transition-colors ${f.usage_type === k ? "border-sky-600 bg-sky-50 text-sky-800" : "border-slate-200 bg-white text-slate-600 hover:border-cyan-400"}`}>{l}</button>
            ))}
          </div>
          <Button data-testid="onboarding-save-btn" disabled={!f.usage_type || !f.first_name || !f.last_name} onClick={save} className="rounded-full bg-sky-600 hover:bg-sky-700">Continua</Button>
        </div>
      )}
    </PageShell>
  );
}
